import { isAxiosError } from 'axios'
import type { TFunction } from 'i18next'
import { ApiCallError } from './apiCall'

const CODE_KEYS: Record<string, string> = {
  code_invalid: 'phone_auth_error_code_invalid',
  code_expired: 'phone_auth_error_code_expired',
  code_send_failed: 'phone_auth_error_code_send_failed',
  patient_required: 'phone_auth_error_patient_required',
  email_not_verified: 'phone_auth_error_email_not_verified',
  patient_not_allowed: 'phone_auth_error_patient_not_allowed',
  already_claimed: 'phone_auth_error_already_claimed',
  already_registered: 'phone_auth_error_already_registered',
  already_confirmed: 'phone_auth_error_already_confirmed',
  dob_required: 'phone_auth_error_dob_required',
  dob_mismatch: 'phone_auth_error_dob_mismatch',
  dob_attempts_exceeded: 'phone_auth_error_dob_attempts_exceeded',
  must_be_adult: 'phone_auth_error_must_be_adult',
  hipaa_required: 'phone_auth_error_hipaa_required',
  terms_required: 'phone_auth_error_terms_required',
  same_number: 'phone_auth_error_same_number',
  phone_required: 'phone_auth_error_phone_required',
  number_has_account: 'phone_auth_error_number_has_account',
  register_by_phone: 'phone_auth_error_register_by_phone',
  google_state_invalid: 'phone_auth_error_google_state_invalid',
  google_state_forbidden: 'phone_auth_error_google_state_forbidden',
  google_email_unverified: 'phone_auth_error_google_email_unverified',
  google_account_in_use: 'phone_auth_error_google_account_in_use',
}

type ErrorInfo = { status: number | undefined; code: string | null; detail: unknown }

// Errors come from the typed client (ApiCallError) or, for the Google callback that still uses axios, from AxiosError
const getErrorInfo = (error: unknown): ErrorInfo | null => {
  if (error instanceof ApiCallError) {
    return { status: error.status, code: error.code, detail: error.detail }
  }

  if (isAxiosError(error)) {
    const data = error.response?.data as { code?: unknown; detail?: unknown } | undefined

    return {
      status: error.response?.status,
      code: typeof data?.code === 'string' ? data.code : null,
      detail: data?.detail,
    }
  }

  return null
}

export const getPhoneAuthErrorCode = (error: unknown): string | null => getErrorInfo(error)?.code ?? null

export const getPhoneAuthErrorStatus = (error: unknown): number | undefined => getErrorInfo(error)?.status

export const isUnauthorized = (error: unknown) => getPhoneAuthErrorStatus(error) === 401

// `detail` is a string, or a list of `{ msg }` for a 422
export const readErrorDetail = (detail: unknown): string | null => {
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => (typeof item === 'object' && item !== null ? (item as { msg?: unknown }).msg : undefined))
      .filter((msg): msg is string => typeof msg === 'string' && msg !== '')

    return messages.length > 0 ? messages.join('. ') : null
  }

  return typeof detail === 'string' && detail ? detail : null
}

export const getPhoneAuthErrorMessage = (error: unknown, t: TFunction): string => {
  const info = getErrorInfo(error)

  if (!info) return t('phone_auth_error_generic')

  if (info.status === 429) return t('phone_auth_error_rate_limited')

  if (info.code && CODE_KEYS[info.code]) return t(CODE_KEYS[info.code])

  return typeof info.detail === 'string' && info.detail ? info.detail : t('phone_auth_error_generic')
}
